/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import arClient from '../../../util/api_client';
import { GMAIL_CONNECT_URL, GMAIL_OAUTH_CONFIG_URL } from '../../../constants/apiUrls';
import { LEGACY_OK, OK } from '../../../constants/platformStatusCodes';

/**
 * Gmail outreach integration state: the backend-provided OAuth config used to
 * build the Google consent URL, plus the connect (auth-code exchange) status.
 *
 * Connected mailboxes themselves are listed through the shared
 * /outreach/getSenderEmailAddresses endpoint (platform === 'gmail') and
 * disconnected through the generic /outreach/removeOAuthConnection — same as
 * Outlook. Sending is untouched: /outreach/sendEmail and /outreach/massOutreach
 * route to Gmail internally from senderInfo {platform: 'gmail', oauthInfoId}.
 */

export const GMAIL_CONNECT_ERRORS = {
  CANCELLED: 'Gmail connection cancelled.',
  GENERIC: "Couldn't connect your Gmail, please try again.",
  NOT_AUTHORIZED:
    'Your Google account is not authorized to connect Gmail. Please try again or contact support.',
};

const initialState = {
  oauthConfig: null, // { client_id, redirect_uri, scopes[] }
  oauthConfigLoading: false,
  connecting: false,
  connectError: null, // user-facing copy rendered inline on the card
  connectedAccount: null, // { oauthInfoId, email } from the last successful connect
};

const gmailSlice = createSlice({
  name: 'Gmail',
  initialState,
  reducers: {
    setOauthConfigLoading: (state, action) => {
      state.oauthConfigLoading = action.payload;
    },
    setOauthConfig: (state, action) => {
      state.oauthConfig = action.payload;
      state.oauthConfigLoading = false;
    },
    setConnecting: (state, action) => {
      state.connecting = action.payload;
    },
    setConnectError: (state, action) => {
      state.connectError = action.payload;
    },
    setConnectedAccount: (state, action) => {
      state.connectedAccount = action.payload;
      state.connectError = null;
    },
    clearConnectState: (state) => {
      state.connecting = false;
      state.connectError = null;
      state.connectedAccount = null;
    },
  },
});

export const {
  setOauthConfigLoading,
  setOauthConfig,
  setConnecting,
  setConnectError,
  setConnectedAccount,
  clearConnectState,
} = gmailSlice.actions;

const isOkStatus = (resp) => resp.status === LEGACY_OK || resp.status === OK || resp.status === 200;

// Dedupes concurrent config fetches (e.g. a Connect click racing the mount
// prefetch) into a single request.
let inFlightConfigPromise = null;

export const fetchGmailOAuthConfig = () => (dispatch, getState) => {
  const cached = getState().Gmail.oauthConfig;
  if (cached) return Promise.resolve(cached);
  if (inFlightConfigPromise) return inFlightConfigPromise;

  dispatch(setOauthConfigLoading(true));
  inFlightConfigPromise = new Promise((resolve, reject) => {
    arClient.doApiCallGet(GMAIL_OAUTH_CONFIG_URL, (resp) => {
      inFlightConfigPromise = null;
      if (isOkStatus(resp) && resp.data?.client_id) {
        dispatch(setOauthConfig(resp.data));
        resolve(resp.data);
      } else {
        dispatch(setOauthConfigLoading(false));
        reject(resp);
      }
    });
  });
  return inFlightConfigPromise;
};

export const connectGmail = ({ authCode, emailAddress }) => (dispatch) =>
  new Promise((resolve, reject) => {
    dispatch(setConnectError(null));
    dispatch(setConnecting(true));
    arClient.doApiCall(GMAIL_CONNECT_URL, { authCode, emailAddress }, (resp) => {
      dispatch(setConnecting(false));
      if (isOkStatus(resp)) {
        dispatch(setConnectedAccount(resp.data || null));
        resolve(resp.data);
      } else {
        const notAuthorized =
          resp.status === 403 ||
          resp.error === 'GMAIL_NOT_AUTHORIZED' ||
          resp.data?.error === 'GMAIL_NOT_AUTHORIZED';
        dispatch(
          setConnectError(
            notAuthorized ? GMAIL_CONNECT_ERRORS.NOT_AUTHORIZED : GMAIL_CONNECT_ERRORS.GENERIC
          )
        );
        const error = new Error(notAuthorized ? 'GMAIL_NOT_AUTHORIZED' : 'GENERIC');
        error.code = notAuthorized ? 'GMAIL_NOT_AUTHORIZED' : 'GENERIC';
        error.resp = resp;
        reject(error);
      }
    });
  });

export const selectGmailOAuthConfig = (state) => state.Gmail.oauthConfig;
export const selectGmailOAuthConfigLoading = (state) => state.Gmail.oauthConfigLoading;
export const selectGmailConnecting = (state) => state.Gmail.connecting;
export const selectGmailConnectError = (state) => state.Gmail.connectError;
export const selectGmailConnectedAccount = (state) => state.Gmail.connectedAccount;

export default gmailSlice.reducer;
